import { useMemo, useRef, useState } from 'react'

import { sentence } from '@/lib/format'
import type { GraphEdge, GraphNode } from '@/types'

/**
 * Node-link view of the opportunity graph.
 *
 * A node-link diagram is an all-pairs colour problem (any node can end up beside
 * any other), and only the first three categorical slots validate all-pairs. So
 * colour carries three groups and **shape plus a direct label** carry the rest -
 * hue never has to do the work alone.
 */

type Group = 'person' | 'company' | 'concept'

const GROUPS: Record<Group, { label: string; colour: string }> = {
  person: { label: 'People', colour: 'var(--series-1)' },
  company: { label: 'Companies', colour: 'var(--series-2)' },
  concept: { label: 'Skills, services, needs & places', colour: 'var(--series-3)' },
}

function groupOf(kind: string): Group {
  if (kind === 'person') return 'person'
  if (kind === 'company') return 'company'
  return 'concept'
}

interface Positioned extends GraphNode {
  x: number
  y: number
  group: Group
  degree: number
}

const WIDTH = 900
const HEIGHT = 560

export function NetworkGraph({
  nodes,
  edges,
  onSelectContact,
}: {
  nodes: GraphNode[]
  edges: GraphEdge[]
  onSelectContact?: (contactId: string) => void
}) {
  const [hover, setHover] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const svgRef = useRef<SVGSVGElement>(null)

  const { placed, links } = useMemo(() => layout(nodes, edges), [nodes, edges])

  const byId = useMemo(() => new Map(placed.map((node) => [node.id, node])), [placed])

  const neighbours = useMemo(() => {
    if (!hover) return null
    const set = new Set<string>([hover])
    for (const edge of links) {
      if (edge.source === hover) set.add(edge.target)
      if (edge.target === hover) set.add(edge.source)
    }
    return set
  }, [hover, links])

  const hovered = hover ? byId.get(hover) : null

  if (!placed.length) {
    return (
      <p className="px-4 py-16 text-center text-sm text-ink-muted">
        The graph is empty. It is projected from profiles, so it fills in once
        conversations have produced structured data.
      </p>
    )
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <Legend />
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="btn-ghost px-2 py-1 text-xs"
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.2))}
            aria-label="Zoom out"
          >
            −
          </button>
          <span className="w-10 text-center text-xs tabular text-ink-muted">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            className="btn-ghost px-2 py-1 text-xs"
            onClick={() => setZoom((z) => Math.min(2.5, z + 0.2))}
            aria-label="Zoom in"
          >
            +
          </button>
        </div>
      </div>

      <div className="relative overflow-auto" style={{ background: 'var(--surface-1)' }}>
        <svg
          ref={svgRef}
          width="100%"
          height={HEIGHT}
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="img"
          aria-label={`Network of ${placed.length} nodes and ${links.length} relationships`}
          style={{ minWidth: WIDTH * zoom, display: 'block' }}
        >
          <g>
            {links.map((edge) => {
              const source = byId.get(edge.source)
              const target = byId.get(edge.target)
              if (!source || !target) return null
              const active = !neighbours || (neighbours.has(edge.source) && neighbours.has(edge.target))
              return (
                <line
                  key={edge.id}
                  x1={source.x}
                  y1={source.y}
                  x2={target.x}
                  y2={target.y}
                  stroke="var(--grid)"
                  strokeWidth={active ? 1.5 : 1}
                  opacity={active ? 0.9 : 0.25}
                />
              )
            })}
          </g>

          <g>
            {placed.map((node) => {
              const dimmed = neighbours ? !neighbours.has(node.id) : false
              const radius = Math.min(5 + node.degree * 1.4, 15)
              const colour = GROUPS[node.group].colour
              const isPerson = node.group === 'person'

              return (
                <g
                  key={node.id}
                  opacity={dimmed ? 0.25 : 1}
                  style={{ cursor: node.ref_id && isPerson ? 'pointer' : 'default' }}
                  onMouseEnter={() => setHover(node.id)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => {
                    if (isPerson && node.ref_id) onSelectContact?.(node.ref_id)
                  }}
                >
                  {/* Shape is the secondary encoding: circle = person, square =
                      company, small diamond = concept. */}
                  {node.group === 'company' ? (
                    <rect
                      x={node.x - radius}
                      y={node.y - radius}
                      width={radius * 2}
                      height={radius * 2}
                      rx="2"
                      fill={colour}
                      stroke="var(--surface-1)"
                      strokeWidth="2"
                    />
                  ) : node.group === 'concept' ? (
                    <rect
                      x={node.x - radius * 0.75}
                      y={node.y - radius * 0.75}
                      width={radius * 1.5}
                      height={radius * 1.5}
                      fill={colour}
                      stroke="var(--surface-1)"
                      strokeWidth="2"
                      transform={`rotate(45 ${node.x} ${node.y})`}
                    />
                  ) : (
                    <circle
                      cx={node.x}
                      cy={node.y}
                      r={radius}
                      fill={colour}
                      stroke="var(--surface-1)"
                      strokeWidth="2"
                    />
                  )}

                  {/* Labels ride the nodes: identity never depends on hue alone. */}
                  {(isPerson || node.degree > 1 || hover === node.id) && (
                    <text
                      x={node.x}
                      y={node.y + radius + 11}
                      textAnchor="middle"
                      fontSize="10"
                      fill="var(--ink-2)"
                      style={{ pointerEvents: 'none' }}
                    >
                      {truncate(node.label, 22)}
                    </text>
                  )}
                </g>
              )
            })}
          </g>
        </svg>

        {hovered && (
          <div
            className="pointer-events-none absolute z-10 max-w-xs rounded border border-line bg-surface px-2.5 py-1.5 text-xs shadow-lg"
            style={{
              left: `${(hovered.x / WIDTH) * 100}%`,
              top: hovered.y * (1 / 1) - 8,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <p className="font-medium text-ink">{hovered.label}</p>
            <p className="text-ink-muted">
              {sentence(hovered.kind)} · {hovered.degree} connection
              {hovered.degree === 1 ? '' : 's'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function Legend() {
  return (
    <ul className="flex flex-wrap items-center gap-4 text-xs text-ink-muted">
      {(Object.keys(GROUPS) as Group[]).map((group) => (
        <li key={group} className="inline-flex items-center gap-1.5">
          <svg width="12" height="12" aria-hidden="true">
            {group === 'person' && <circle cx="6" cy="6" r="5" fill={GROUPS[group].colour} />}
            {group === 'company' && (
              <rect x="1" y="1" width="10" height="10" rx="1.5" fill={GROUPS[group].colour} />
            )}
            {group === 'concept' && (
              <rect
                x="2.5" y="2.5" width="7" height="7"
                fill={GROUPS[group].colour}
                transform="rotate(45 6 6)"
              />
            )}
          </svg>
          {GROUPS[group].label}
        </li>
      ))}
    </ul>
  )
}

function truncate(text: string, limit: number): string {
  return text.length <= limit ? text : `${text.slice(0, limit - 1)}…`
}

/**
 * A small deterministic force layout.
 *
 * Deterministic on purpose: the same graph must draw the same way every render,
 * or the picture appears to change when only the viewport did.
 */
function layout(nodes: GraphNode[], edges: GraphEdge[]) {
  const capped = nodes.slice(0, 220)
  const allowed = new Set(capped.map((n) => n.id))
  const links = edges.filter((e) => allowed.has(e.source) && allowed.has(e.target))

  const degree = new Map<string, number>()
  for (const edge of links) {
    degree.set(edge.source, (degree.get(edge.source) ?? 0) + 1)
    degree.set(edge.target, (degree.get(edge.target) ?? 0) + 1)
  }

  // Seed on a circle, heaviest nodes first so hubs start near the centre.
  const ordered = [...capped].sort(
    (a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0),
  )

  const placed: Positioned[] = ordered.map((node, index) => {
    const ring = Math.floor(index / 18)
    const angle = (index % 18) * ((Math.PI * 2) / 18) + ring * 0.35
    const radius = 70 + ring * 78
    return {
      ...node,
      group: groupOf(node.kind),
      degree: degree.get(node.id) ?? 0,
      x: WIDTH / 2 + Math.cos(angle) * radius,
      y: HEIGHT / 2 + Math.sin(angle) * radius * 0.62,
    }
  })

  const index = new Map(placed.map((node, i) => [node.id, i]))

  for (let iteration = 0; iteration < 160; iteration++) {
    const cooling = 1 - iteration / 160

    // Repulsion, O(n^2) but n is capped at 220 so it stays well under a frame.
    for (let i = 0; i < placed.length; i++) {
      for (let j = i + 1; j < placed.length; j++) {
        const a = placed[i]
        const b = placed[j]
        let dx = a.x - b.x
        let dy = a.y - b.y
        let distance = Math.hypot(dx, dy) || 0.01
        if (distance > 260) continue
        const force = (1400 / (distance * distance)) * cooling
        dx = (dx / distance) * force
        dy = (dy / distance) * force
        a.x += dx
        a.y += dy
        b.x -= dx
        b.y -= dy
      }
    }

    // Attraction along edges.
    for (const edge of links) {
      const a = placed[index.get(edge.source)!]
      const b = placed[index.get(edge.target)!]
      const dx = b.x - a.x
      const dy = b.y - a.y
      const distance = Math.hypot(dx, dy) || 0.01
      const force = ((distance - 85) * 0.012) * cooling
      a.x += (dx / distance) * force
      a.y += (dy / distance) * force
      b.x -= (dx / distance) * force
      b.y -= (dy / distance) * force
    }

    // Gentle pull to centre so disconnected nodes do not drift off-canvas.
    for (const node of placed) {
      node.x += (WIDTH / 2 - node.x) * 0.006 * cooling
      node.y += (HEIGHT / 2 - node.y) * 0.006 * cooling
      node.x = Math.max(30, Math.min(WIDTH - 30, node.x))
      node.y = Math.max(24, Math.min(HEIGHT - 24, node.y))
    }
  }

  return { placed, links }
}
